import { transformArrayToCamelCase } from '@skytech/utils'

import { IS_NEW_BACKEND_API } from '@/modules/admin/resources/drill-down'
import { get, post } from '@/util/auth'

import {
  API_ROUTE_LEASING_CUSTOMER_VENDORS,
  API_ROUTE_LEASING_END_COUNT,
  API_ROUTE_LEASING_EXTEND,
  API_ROUTE_LEASING_EXTEND_VALIDATE,
  API_ROUTE_LEASING_RETURN,
  API_ROUTE_LEASING_RETURN_VALIDATE,
  API_ROUTE_LEASING_VENDORS,
  API_ROUTE_LEASING_ASSETS_DETAILS,
  API_ROUTE_LEASING_END_OF_LEASE,
  API_ROUTE_LEASING_END_OF_LEASE_VALIDATE,
} from './api-routes'

type CustomerVendorOriginal = {
  customerId: number
  vendorId: number
  name: string
  usePolicyLevelLeasing: boolean
}
type CustomerVendorListOriginal = {
  success: boolean
  vendors: CustomerVendorOriginal[]
}
type AssetValidate = {
  assetId: number
  message: string
}

type LeasingInfo = {
  leaseStart: string
  leaseEnd: string
  leasePeriod: number
  endOfLeaseProcessStartDate: string
  endOfLeaseEmployeeDeadlineDate: string
  endOfLeaseAvailableEndingOptions: string
  endOfLeaseCurrentEndingOption: string
  terminationDate: string
  customerBuyoutPrice: number
}
type EndOfLeaseWorkflowHistory = {
  endOfLeaseDate: string
  processStartDate: string
  employeeDecisionOpenDate: string
  employeeDecisionDeadlineDate: string
  currentEndingOption: string
  processStartedDatetime: string
  isActive: boolean
  completedDatetime: string
  cancelledDatetime: string
  workflowStep: string
}
type LeasingFieldLocks = {
  leaseStart: boolean
  leaseEnd: boolean
  assetType: boolean
  assetDescription: boolean
  assetOwnership: boolean
  assetCustomer: boolean
  imeiSerialNumber: boolean
  assetUser: boolean
}
type LeasingAssetDetailsOriginal = {
  assetId: number
  leasingInfo?: LeasingInfo
  endOfLeaseWorkflowHistory?: EndOfLeaseWorkflowHistory[]
  currentEndOfLeaseWorkflow?: EndOfLeaseWorkflowHistory
  leasingFieldLocks?: LeasingFieldLocks
  lockFields?: Array<string>
  orderId?: number
}

export const leasingResources = {
  getAssetsToReturnCount: () => get<number>(API_ROUTE_LEASING_END_COUNT, IS_NEW_BACKEND_API),
  getCustomerVendors: async () =>
    (await get<CustomerVendorListOriginal>(API_ROUTE_LEASING_VENDORS, IS_NEW_BACKEND_API)).vendors,
  getVendorsByCustomerId: async (customerId: number) => {
    const response = await get<CustomerVendorListOriginal>(
      API_ROUTE_LEASING_CUSTOMER_VENDORS.replace(':id', String(customerId)),
      true
    )
    return response.vendors
  },
  getLeasingAssetDetails: async (id: number) => {
    const leasingAssetDetailsOriginal = await get<LeasingAssetDetailsOriginal>(
      API_ROUTE_LEASING_ASSETS_DETAILS.replace(':id', String(id)),
      IS_NEW_BACKEND_API
    )

    let parsedLockedFields = leasingAssetDetailsOriginal.lockFields
    if (parsedLockedFields) {
      parsedLockedFields = transformArrayToCamelCase(parsedLockedFields)
    }

    return { lockFields: parsedLockedFields, ...leasingAssetDetailsOriginal }
  },

  // Mutations
  endOfLeaseAssets: (payload: { assetIds: number[] }) =>
    post(API_ROUTE_LEASING_END_OF_LEASE, payload, IS_NEW_BACKEND_API),
  endOfLeaseAssetsValidate: (payload: { assetIds: number[] }) =>
    post<AssetValidate[]>(API_ROUTE_LEASING_END_OF_LEASE_VALIDATE, payload, IS_NEW_BACKEND_API),
  extendAssets: (payload: { assetIds: number[]; numberOfMonths: number }) =>
    post(API_ROUTE_LEASING_EXTEND, payload, IS_NEW_BACKEND_API),
  extendAssetsValidate: (payload: { assetIds: number[]; numberOfMonths: number }) =>
    post<AssetValidate[]>(API_ROUTE_LEASING_EXTEND_VALIDATE, payload, IS_NEW_BACKEND_API),
  returnAssets: (payload: { assetIds: number[] }) =>
    post(API_ROUTE_LEASING_RETURN, payload, IS_NEW_BACKEND_API),
  returnAssetsValidate: (payload: { assetIds: number[] }) =>
    post<AssetValidate[]>(API_ROUTE_LEASING_RETURN_VALIDATE, payload, IS_NEW_BACKEND_API),
}

export type Return = Awaited<ReturnType<typeof leasingResources.getAssetsToReturnCount>>

export type CustomerVendor = Awaited<ReturnType<typeof leasingResources.getCustomerVendors>>
export type VendorsByCustomerId = Awaited<
  ReturnType<typeof leasingResources.getVendorsByCustomerId>
>
export type LeasingAssetDetailsReturn = Awaited<
  ReturnType<typeof leasingResources.getLeasingAssetDetails>
>

export type EndOfLeaseAssetsValidate = Awaited<
  ReturnType<typeof leasingResources.endOfLeaseAssetsValidate>
>
export type ExtendAssetsValidate = Awaited<ReturnType<typeof leasingResources.extendAssetsValidate>>
export type ReturnAssetsValidate = Awaited<ReturnType<typeof leasingResources.returnAssetsValidate>>
